import React, { useState } from 'react';
import { Modal, Select, Spin, Button } from 'antd';
import { useGetCurrencies } from '../../hooks/genericHooks';
import { getAnchoredModalStyle } from './modalPositioning';

// Accept unified dynamic modal props
interface CurrencySelectProps {
  open: boolean;
  value?: any;
  onOk: (value: any) => void;
  onCancel: () => void;
  afterClose?: () => void;
  options?: any[];
  returnMode?: 'display' | 'currency_code';
  getContainer?: any;
  anchorRect?: DOMRect | null;
  zIndex?: number;
}

const CurrencySelect: React.FC<CurrencySelectProps> = ({
  open,
  value,
  onOk,
  onCancel,
  afterClose,
  returnMode = 'display',
  getContainer,
  anchorRect,
  zIndex,
}) => {
  const { data: currenciesData, isLoading } = useGetCurrencies();
  const [selectedId, setSelectedId] = useState<string | number | null>(value ?? null);

  const inCustomContainer = typeof getContainer === 'function';
  const modalWidth = 300;

  // Keep internal state in sync with prop
  React.useEffect(() => {
    setSelectedId(value ?? null);
  }, [value]);

  const currenciesList = Array.isArray(currenciesData)
    ? currenciesData
    : (currenciesData?.data || []);

  const confirmSelection = React.useCallback(() => {
    const selectedCurrency = (currenciesList || []).find((currency: any) => currency.id === selectedId);
    if (!selectedCurrency) return;
    if (returnMode === 'currency_code') {
      onOk(String(selectedCurrency.currency_code || '').toUpperCase());
    } else {
      onOk(`${selectedCurrency.name} (${selectedCurrency.currency_code})`);
    }
  }, [currenciesList, onOk, returnMode, selectedId]);

  // If caller provides a currency code (e.g. 'USD'), map it to internal id
  React.useEffect(() => {
    if (!open) return;
    if (!value) return;
    if (typeof value !== 'string') return;
    const code = value.trim().toUpperCase();
    if (code.length !== 3) return;
    const hit = (currenciesList || []).find((c: any) => String(c?.currency_code || '').toUpperCase() === code);
    if (hit?.id !== undefined && hit?.id !== null) setSelectedId(hit.id);
  }, [open, value, currenciesList]);

  const modalStyle = React.useMemo((): React.CSSProperties | undefined => {
    if (!inCustomContainer) return undefined;

    const anchored = getAnchoredModalStyle(anchorRect, { width: modalWidth });
    if (anchored) return anchored;
    return { top: 12, right: 12, margin: 0, position: 'fixed' } as React.CSSProperties;
  }, [anchorRect, inCustomContainer, modalWidth]);

  return (
    <Modal
      open={open}
      title="Select Currency"
      closable={true}
      width={300}
      getContainer={getContainer ?? (() => document.body)}
      zIndex={typeof zIndex === 'number' ? zIndex : 2000}
      centered={!inCustomContainer}
      style={modalStyle}
      afterClose={afterClose}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="ok"
          type="primary"
          onClick={confirmSelection}
          disabled={!selectedId}
        >
          Select
        </Button>
      ]}
      destroyOnClose
      onCancel={onCancel}
    >
      <Spin spinning={isLoading}>
        <Select
          showSearch
          style={{ width: '100%', borderRadius: 0, background: '#e6f2ff', border: 'none' }}
          placeholder="Select a currency"
          value={selectedId ?? undefined}
          onChange={setSelectedId}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && selectedId) {
              e.preventDefault();
              confirmSelection();
            }
          }}
          optionFilterProp="children"
          dropdownStyle={{ borderRadius: 0 }}
          filterOption={(input, option) =>
            String(option?.children).toLowerCase().includes(input.toLowerCase())
          }
        >
          {currenciesList.map((currency: any) => (
            <Select.Option
              key={currency.id}
              value={currency.id}
              title={`${currency.name} (${currency.currency_code}) - ${currency.symbol}`}
            >
              {currency.name} ({currency.currency_code}) - {currency.symbol}
            </Select.Option>
          ))}
        </Select>
      </Spin>
    </Modal>
  );
};

export default CurrencySelect;
